import * as React from "react";
import AppIcon from "../components/AppIcon";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import axios from "axios";
import { useNavigate } from "react-router-dom";

interface FloorPlan {
    id: string;
    name: string;
    image: string;
    isActive: boolean;
}

const FloorPlan = () => {
    const navigate = useNavigate();
    const [floorPlans, setFloorPlans] = React.useState<FloorPlan[]>([]);
    const [activeFloorPlan, setActiveFloorPlan] = React.useState<FloorPlan | null>(null);

    React.useEffect(() => {
        axios
            .get(`${Server}/api/floorPlans`)
            .then((res) => {
                if (res.status === 200) {
                    const fps = res.data.filter((f: { isActive: any }) => f.isActive);
                    setFloorPlans(fps);
                    setActiveFloorPlan(fps[0]);
                } else {
                    alert("Error getting FloorPlans");
                }
            })
            .catch(() => {
                alert("Error getting FloorPlans");
            });
    }, []);

    return (
        <div
            className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
            style={{
                backgroundImage: "url(/images/collectivebg-2.jpg)",
            }}
        >
            <div className="min-h-screen backdrop-blur-sm" style={{ backgroundColor: "rgba(241, 245, 248, 0.9)" }}>
                {/* Header Section */}
                <div className="pt-8 pb-6">
                    <div className="max-w-6xl mx-auto px-8">
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigate("/home")}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                                style={{ backgroundColor: "rgba(121, 153, 186, 0.9)" }}
                            >
                                <span className="text-white text-xl"><AppIcon kind="home" /></span>
                            </button>
                            <div className="text-center w-full">
                                <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: "#013A51" }}>
                                    Event Map
                                </h1>
                                <div className="w-24 h-1 mx-auto rounded-full" style={{ backgroundColor: "#DB1C7D" }}></div>
                            </div>
                            <div className="w-24"></div> {/* Spacer for centering */}
                        </div>

                        {/* Floor Plan Selection */}
                        {floorPlans.length > 1 && (
                            <div className="flex flex-wrap gap-3 justify-center mb-8">
                                {floorPlans.map((fp: FloorPlan) => (
                                    <button
                                        key={fp.id}
                                        onClick={() => setActiveFloorPlan(fp)}
                                        className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg backdrop-blur-sm ${
                                            fp.id === activeFloorPlan?.id ? "text-white shadow-lg" : "text-white hover:shadow-xl"
                                        }`}
                                        style={{
                                            backgroundColor: fp.id === activeFloorPlan?.id ? "rgba(219, 28, 125, 0.9)" : "rgba(112, 131, 114, 0.8)",
                                        }}
                                    >
                                        {fp.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Map Display Section */}
                <div className="px-8 pb-8">
                    <div className="max-w-6xl mx-auto">
                        {activeFloorPlan && (
                            <div className="">
                                <div className="flex justify-center w-full overflow-auto rounded-lg max-h-[80vh]" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                    <TransformWrapper minScale={0.2} maxScale={5} wheel={{ step: 0.1 }} doubleClick={{ disabled: false, step: 1 }} pinch={{ step: 5 }}>
                                        <TransformComponent>
                                            <div className="h-[80vh]">
                                                <img src={activeFloorPlan.image} alt="FloorPlan" className="max-w-full max-h-[80vh] rounded-lg shadow-lg" />
                                            </div>
                                        </TransformComponent>
                                    </TransformWrapper>
                                </div>

                                {/* Map Instructions */}
                                <div className="mt-6 text-center">
                                    <p className="text-sm font-medium mb-2" style={{ color: "#013A51" }}>
                                        Interactive Map Instructions
                                    </p>
                                    <div className="flex flex-wrap justify-center gap-4 text-xs" style={{ color: "#708372" }}>
                                        <span><AppIcon kind="mouse" /> Scroll to zoom</span>
                                        <span><AppIcon kind="hand" /> Drag to pan</span>
                                        <span><AppIcon kind="mouse" /> Double-click to zoom</span>
                                        <span><AppIcon kind="mobile" /> Pinch to zoom on mobile</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!activeFloorPlan && floorPlans.length === 0 && (
                            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-12 shadow-lg text-center" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                <div className="mb-4">
                                    <span className="text-6xl"><AppIcon kind="map" /></span>
                                </div>
                                <h3 className="text-2xl font-bold mb-2" style={{ color: "#013A51" }}>
                                    No Maps Available
                                </h3>
                                <p className="text-lg" style={{ color: "#708372" }}>
                                    Event maps will be available soon. Please check back later.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FloorPlan;
